import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Link,
  Icon,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FaFileAlt,
  FaBook,
  FaChartLine,
  FaRoad,
  FaCode,
  FaTools,
  FaGraduationCap,
  FaBug,
  FaWallet,
  FaSearch,
  FaUserTie,
  FaCoins,
  FaServer,
  FaCubes,
} from 'react-icons/fa';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';

interface ResourceLink {
  label: string;
  href: string;
  icon: React.ElementType;
  description: string;
}

interface ResourceSection {
  title: string;
  links: ResourceLink[];
}

const ResourceCard = ({ link }: { link: ResourceLink }) => {
  const isExternal = link.href.startsWith('http');

  const cardContent = (
    <Box
      p={5}
      bg="rgba(255, 255, 255, 0.03)"
      backdropFilter="blur(10px)"
      borderRadius="xl"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.08)"
      transition="all 0.3s ease"
      cursor="pointer"
      _hover={{
        transform: 'translateY(-4px)',
        bg: 'rgba(255, 255, 255, 0.06)',
        borderColor: 'rgba(59, 130, 246, 0.4)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
      }}
      h="full"
    >
      <VStack align="start" spacing={3}>
        <Icon as={link.icon} boxSize={6} color="#3b82f6" />
        <Text fontWeight="700" color="white" fontSize="md">
          {link.label}
        </Text>
        <Text fontSize="sm" color="gray.400" lineHeight="1.6">
          {link.description}
        </Text>
      </VStack>
    </Box>
  );

  if (isExternal) {
    return (
      <Link
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        _hover={{ textDecoration: 'none' }}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <Link
      as={RouterLink}
      to={link.href}
      _hover={{ textDecoration: 'none' }}
    >
      {cardContent}
    </Link>
  );
};

const Resources = () => {
  const sections: ResourceSection[] = [
    {
      title: 'Key Documents',
      links: [
        { label: 'Pitch Deck', href: '/pitch-deck', icon: FaFileAlt, description: 'Overview presentation of Block52 for investors and partners.' },
        { label: 'White Paper', href: '/white-paper', icon: FaBook, description: 'Technical documentation explaining the Block52 architecture.' },
        { label: 'Token Economics', href: '/tokenomics', icon: FaChartLine, description: 'B52 token distribution, utility, and economic model.' },
        { label: 'Development Timeline', href: '/timeline', icon: FaRoad, description: 'Roadmap and milestones for Block52 development.' },
      ],
    },
    {
      title: 'Learn',
      links: [
        { label: 'What is Block52?', href: '/learn/what-is-block52', icon: FaCubes, description: 'Introduction to the Block52 ecosystem.' },
        { label: 'Poker Virtual Machine', href: '/learn/poker-virtual-machine', icon: FaServer, description: 'How the PVM powers decentralized card games.' },
        { label: 'What is B52 Token?', href: '/learn/what-is-b52-token', icon: FaCoins, description: 'The utility and governance token of Block52.' },
        { label: 'Block52 Wallets', href: '/learn/block52-wallets', icon: FaWallet, description: 'Setting up and using wallets with Block52.' },
        { label: 'Smart Contracts', href: '/learn/smart-contracts', icon: FaCode, description: 'How smart contracts power provably fair games.' },
        { label: 'Gas Fees', href: '/learn/gas-fees', icon: FaCoins, description: 'Understanding transaction costs on Block52.' },
        { label: 'Run a Node', href: '/learn/run-a-node', icon: FaServer, description: 'Guide to running your own Block52 node.' },
        { label: 'Block52 Glossary', href: '/learn/block52-glossary', icon: FaBook, description: 'Definitions of key terms and concepts.' },
      ],
    },
    {
      title: 'Use Block52',
      links: [
        { label: 'Poker in a Box', href: '/p-in-a-box', icon: FaCubes, description: 'Launch your own branded card game club.' },
        { label: 'Buy B52 Tokens', href: '/token-sale', icon: FaCoins, description: 'Participate in the B52 token sale.' },
        { label: 'Validator vs Delegator', href: '/validator-delegator', icon: FaUserTie, description: 'Choose how to participate in network security.' },
        { label: 'Find a Poker Club', href: '/use/find-a-poker-club', icon: FaSearch, description: 'Discover clubs running on Block52.' },
        { label: 'Become an Agent', href: '/use/become-an-agent', icon: FaUserTie, description: 'Earn by referring players and clubs.' },
        { label: 'Stake B52', href: '/use/stake-b52', icon: FaCoins, description: 'Earn rewards by staking your tokens.' },
        { label: 'Validator Dashboard', href: '/use/validator-dashboard', icon: FaServer, description: 'Monitor your validator performance.' },
        { label: 'Block Explorer', href: '/use/block-explorer', icon: FaSearch, description: 'Explore transactions and blocks on Block52.' },
      ],
    },
    {
      title: 'Build',
      links: [
        { label: 'Developer Docs', href: '/build/developer-docs', icon: FaBook, description: 'Complete documentation for developers.' },
        { label: 'API Documentation', href: '/build/api-documentation', icon: FaCode, description: 'REST and WebSocket API reference.' },
        { label: 'SDK & Libraries', href: '/build/sdk-libraries', icon: FaTools, description: 'Official SDKs for integrating with Block52.' },
        { label: 'Smart Contract Templates', href: '/build/smart-contract-templates', icon: FaCode, description: 'Pre-built contracts for common use cases.' },
        { label: 'Tutorials', href: '/build/tutorials', icon: FaGraduationCap, description: 'Step-by-step guides for building on Block52.' },
        { label: 'Local Testnet', href: '/build/local-testnet', icon: FaServer, description: 'Set up a local development environment.' },
        { label: 'Developer Grants', href: '/build/developer-grants', icon: FaCoins, description: 'Funding for projects building on Block52.' },
        { label: 'Bug Bounty Program', href: '/build/bug-bounty-program', icon: FaBug, description: 'Earn rewards for finding vulnerabilities.' },
      ],
    },
  ];

  return (
    <Box minH="100vh" bg="#0a0a0f" position="relative">
      <NavbarWithCallToAction />

      <Container maxW="6xl" py={{ base: 12, md: 20 }}>
        <VStack spacing={16} align="stretch">
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <Text
              fontSize="sm"
              color="#64ffda"
              textTransform="uppercase"
              letterSpacing="0.15em"
              fontWeight="700"
            >
              Documentation & Guides
            </Text>
            <Heading
              as="h1"
              fontSize={{ base: '3xl', md: '5xl' }}
              fontWeight="900"
              color="white"
              letterSpacing="-0.02em"
            >
              Resources
            </Heading>
            <Text color="gray.400" maxW="2xl" fontSize="lg">
              Everything you need to understand, use, and build on Block52.
            </Text>
          </VStack>

          {/* Resource Sections */}
          {sections.map((section, idx) => (
            <VStack key={idx} spacing={6} align="stretch">
              <Heading
                size="lg"
                color="white"
                fontWeight="700"
                borderBottom="1px solid"
                borderColor="rgba(255, 255, 255, 0.1)"
                pb={3}
              >
                {section.title}
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                {section.links.map((link, linkIdx) => (
                  <ResourceCard key={linkIdx} link={link} />
                ))}
              </SimpleGrid>
            </VStack>
          ))}
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Resources;
